import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import keyboardKey from 'keyboard-key';
import get from 'lodash/get';
import set from 'lodash/set';

import './styles.css';

import messages from './messages';
import CollegeEventRow from './collegeEventRow';
import CollegeEventsAnnouncement from './collegeEventsAnnouncement';
import Filters from '../Filters';
import { Modal, Grid, Icon, Loader } from '../../components';
import { isDeepEmpty } from '../utils';
import { COLLEGE_EVENTS_PAGE_SIZE } from './graphql/fetchCollegeEvents';
import { getSingleClickEvent, getBatchClickEvent } from '../../utils/clicktrackingManager';

function parseGraphQL(searchCollegeEvents) {
  const events = parseEvents(searchCollegeEvents);
  const filteredEvents = JSON.parse(JSON.stringify(events));
  return {
    events,
    filteredEvents,
  };
}

function parseEvents(searchCollegeEvents) {
  if (!searchCollegeEvents) return [];
  return searchCollegeEvents.edges.map((event) => ({
    ...event.node.collegeEvent,
    recommended: event.node.recommended,
    recommendedAudienceProfile: event.node.recommendedAudienceProfile,
    college: event.node.college,
  }));
}

class CollegeEvents extends React.Component {
  static propTypes = {
    intl: PropTypes.object,
    searchCollegeEvents: PropTypes.object,
    majorsData: PropTypes.object,
    recommendedEventsData: PropTypes.object,
    fcService: PropTypes.shape({
      clickTrackingEvents: PropTypes.func,
    }),
  };

  constructor(props) {
    super(props);
    this.container = undefined;
    this.state = {
      ...parseGraphQL(props.searchCollegeEvents),
      showEventsAnnouncement: props.isNewSession,
      openModal: false,
      loadingMoreEvents: false,
      dateSort: { asc: true, active: true },
      collegeSort: { asc: false, active: false },
      filters: {},
      queryFilters: {},
      viewAllEvents: false,
      filteredByRecommended: false,
    };
  }
  componentDidUpdate(prevProps) {
    if (prevProps.searchCollegeEvents !== this.props.searchCollegeEvents) {
      const { events, filteredEvents } = parseGraphQL(this.props.searchCollegeEvents);
      this.setState({ events, filteredEvents, loadingMoreEvents: false });
      const currentCount = get(this.props, 'searchCollegeEvents.edges.length', 0);
      const previousCount = get(prevProps, 'searchCollegeEvents.edges.length', 0);
      const showingMore = currentCount > COLLEGE_EVENTS_PAGE_SIZE && previousCount;
      const clickTrackingEvents = showingMore ? filteredEvents.slice(previousCount) : filteredEvents;
      this.handleEventsShown(clickTrackingEvents);
    }
    if (prevProps.isNewSession !== this.props.isNewSession) {
      this.setState({ showEventsAnnouncement: this.props.isNewSession });
    }
  }

  sortEventsByDateRemotely(asc = true) {
    this.setState({ dateSort: { asc, active: true }, collegeSort: { active: false } });
    this.props.refetch({ sort: { sortBy: 'EVENT_START_DATE', direction: asc ? 'ASCENDING' : 'DESCENDING' } });
  }

  sortEventsByCollegeNameRemotely(asc = true) {
    this.setState({ collegeSort: { asc, active: true }, dateSort: { active: false } });
    this.props.refetch({ sort: { sortBy: 'COLLEGE_NAME', direction: asc ? 'ASCENDING' : 'DESCENDING' } });
  }

  /***** FILTERS */
  filterByRecommended = (value, removeFilters = true, fromBanner = false) => {
    const update = {
      showEventsAnnouncement: false,
      filteredByRecommended: fromBanner,
      viewAllEvents: false,
      ...(removeFilters ? { filters: {}, queryFilters: {} } : {}),
    };
    this.setState({ ...update }, () => this.filterEvents('recommended', value, 'recommended'));
  };

  filterByVirtualEvents = (value, removeFilters = true) => {
    const update = {
      viewAllEvents: false,
      ...(removeFilters ? { filters: {}, queryFilters: {} } : {}),
    };
    this.setState({ ...update }, () => this.filterEvents('virtual', value, 'virtual'));
  };

  filterEvents = (name, value, id) => {
    const { filters, queryFilters } = this.state;
    set(queryFilters, name, value);
    set(filters, id, value);
    if (isDeepEmpty(value)) delete queryFilters[name.split('.')[0]];
    if (!queryFilters.virtual) delete queryFilters.virtual;
    this.setState({ filters, queryFilters });
    const filter = JSON.parse(JSON.stringify(queryFilters));
    this.props.refetch({ filter });
  };

  clearFilters = () => {
    this.setState({ filters: {}, queryFilters: {}, filteredByRecommended: false });
    const filter = JSON.parse(JSON.stringify(this.state.queryFilters));
    if (!isDeepEmpty(filter)) return this.props.refetch({ filter: {} });
  };

  viewAllEvents = () => {
    this.setState({ viewAllEvents: true });
    this.clearFilters();
  };

  loadMore = () => {
    this.setState({ loadingMoreEvents: true });
    this.props.loadMore('searchCollegeEvents');
  };

  recommendedEventsCount = () => {
    return get(this.props, 'recommendedEventsData.recommendedCollegeEvents.totalCount', 0);
  };

  hasCoppaFerpaBlock = () => get(this.props, 'userInfo.userData.coppa', false);
  hasFerpaBlock = () => get(this.props, 'userInfo.userData.hasFerpaBlock', false);

  // Single Event
  handleClickTracking = (clickEvent) => {
    this.props.fcService.clickTrackingEvents(getSingleClickEvent(clickEvent, this.props.userInfo, this.state.filters));
  };

  // Batched Events
  handleEventsShown = (eventsShown) => {
    if (this.hasCoppaFerpaBlock()) return;
    this.props.fcService.clickTrackingEvents(getBatchClickEvent(eventsShown, this.props.userInfo, this.state.filters));
  };

  handleSortKey = (event, action) => {
    const key = keyboardKey.getCode(event);
    if (key !== keyboardKey.Spacebar && key !== keyboardKey.Enter) return;
    action();
    event.preventDefault();
  };

  render() {
    const {
      filteredEvents,
      showEventsAnnouncement,
      collegeSort,
      dateSort,
      filters,
      loadingMoreEvents,
      viewAllEvents,
      filteredByRecommended,
    } = this.state;

    const { majorsData } = this.props;
    const totalEventCount = get(this.props, 'searchCollegeEvents.totalCount', 0);
    const recommendedEventsCount = this.recommendedEventsCount();
    const currentEventCount = filteredEvents.length;
    const sortByDate = () => this.sortEventsByDateRemotely(!dateSort.asc);
    const sortByName = () => this.sortEventsByCollegeNameRemotely(!collegeSort.asc);
    return (
      <div className="college-events-container" ref={(el) => (this.container = el)}>
        <div className="college-events-header">
          <div className="header-text" data-cy="events-header-text">
            <FormattedMessage {...messages.header} />
          </div>
        </div>

        {!!recommendedEventsCount && !!showEventsAnnouncement && !this.hasCoppaFerpaBlock() && (
          <CollegeEventsAnnouncement
            recommendedEventsCount={recommendedEventsCount}
            filterByRecommended={this.filterByRecommended}
            handleClickTracking={this.handleClickTracking}
          />
        )}

        <div className="college-events-inner">
          <div>
            <Filters
              openModal={/* istanbul ignore next */ () => this.setState({ openModal: true })}
              filterByRecommended={this.filterByRecommended}
              filterByVirtualEvents={this.filterByVirtualEvents}
              clearFilters={this.clearFilters}
              majors={majorsData.majors}
              filters={filters}
              filterEvents={this.filterEvents}
              viewAllEvents={viewAllEvents}
              filteredByRecommended={filteredByRecommended}
            />
          </div>
          <div className="college-events-list">
            <Grid>
              <Grid.Row data-cy="events-list-header">
                <Grid.Column computer={2} mobile={12}>
                  <div className="date">
                    <div
                      data-cy="event-date-sort-column"
                      onKeyDown={(event) => this.handleSortKey(event, sortByDate)}
                      onClick={sortByDate}
                      tabIndex="0"
                    >
                      <FormattedMessage {...messages.eventdate} />
                    </div>
                    <div className={`order order${!dateSort.active && '-space-inactive'}`}>
                      {(!dateSort.active || (dateSort.active && !dateSort.asc)) && (
                        <Icon
                          name="caret up"
                          data-cy="event-date-sort-asc"
                          className={dateSort.active ? 'space-bottom' : ''}
                          onClick={() => this.sortEventsByDateRemotely(true)}
                        />
                      )}
                      {(!dateSort.active || (dateSort.active && dateSort.asc)) && (
                        <Icon
                          name="caret down"
                          data-cy="event-date-sort-desc"
                          className={dateSort.active ? 'space-top' : ''}
                          onClick={() => this.sortEventsByDateRemotely(false)}
                        />
                      )}
                    </div>
                  </div>
                </Grid.Column>
                <Grid.Column width={8}>
                  <div className="name">
                    <div
                      data-cy="event-name-sort-column"
                      tabIndex="0"
                      onKeyDown={(event) => this.handleSortKey(event, sortByName)}
                      onClick={sortByName}
                    >
                      <FormattedMessage {...messages.collegename} />
                    </div>
                    <div className={`order order${!collegeSort.active && '-space-inactive'}`}>
                      {(!collegeSort.active || (collegeSort.active && !collegeSort.asc)) && (
                        <Icon
                          name="caret up"
                          data-cy="event-name-sort-asc"
                          className={collegeSort.active ? 'space-bottom' : ''}
                          onClick={() => this.sortEventsByCollegeNameRemotely(true)}
                        />
                      )}
                      {(!collegeSort.active || (collegeSort.active && collegeSort.asc)) && (
                        <Icon
                          name="caret down"
                          data-cy="event-name-sort-desc"
                          className={collegeSort.active ? 'space-top' : ''}
                          onClick={() => this.sortEventsByCollegeNameRemotely(false)}
                        />
                      )}
                    </div>
                  </div>
                </Grid.Column>
                <Grid.Column width={2}>
                  <div className="location">
                    <FormattedMessage {...messages.eventlocation} />
                  </div>
                </Grid.Column>
                <Grid.Column width={4}>
                  <div className="singup" />
                </Grid.Column>
              </Grid.Row>
              {this.props.loading && <Loader style={{ marginTop: 24, display: 'flex', flex: 1 }} inline active indeterminate />}
              {!this.props.loading && filteredEvents.length === 0 && (
                <div className="no-events">
                  <FormattedMessage {...messages.noevents} />
                  <div data-cy="view-all-events" onClick={this.viewAllEvents}>
                    {' '}
                    <FormattedMessage {...messages.viewallevents} />
                  </div>
                </div>
              )}
              {filteredEvents.map((event, i) => (
                <Grid.Row key={i} className="event-row" data-cy="event-row">
                  <CollegeEventRow
                    hasCoppaFerpaBlock={this.hasCoppaFerpaBlock()}
                    hasFerpaBlock={this.hasFerpaBlock()}
                    event={event}
                    handleClickTracking={this.handleClickTracking}
                  />
                </Grid.Row>
              ))}
            </Grid>
          </div>
          <div className="college-events-pagination-container">
            {loadingMoreEvents && <Loader style={{ marginBottom: 24 }} inline active indeterminate />}
            {!this.props.loading && filteredEvents.length !== 0 && (
              <>
                <div className="college-events-pagination-hint">
                  <FormattedMessage {...messages.paginationHint} values={{ 0: currentEventCount, 1: totalEventCount }} />
                </div>
                {filteredEvents.length < totalEventCount && (
                  <div className="college-events-pagination-button" data-cy="events-pagination-button" onClick={() => this.loadMore()}>
                    <FormattedMessage {...messages.paginationButton} />
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <Modal open={this.state.openModal} mountNode={this.container} data-cy="mobile-filters-modal">
          <Modal.Content>
            <Modal.Description>
              <Filters
                modal
                closeModal={/* istanbul ignore next */ () => this.setState({ openModal: false })}
                filterByRecommended={this.filterByRecommended}
                filterByVirtualEvents={this.filterByVirtualEvents}
                clearFilters={this.clearFilters}
                majors={majorsData.majors}
                filters={filters}
                filterEvents={this.filterEvents}
                viewAllEvents={viewAllEvents}
                filteredByRecommended={filteredByRecommended}
              />
            </Modal.Description>
          </Modal.Content>
        </Modal>
      </div>
    );
  }
}

export default injectIntl(CollegeEvents);
